import {Component, inject, OnInit, signal} from '@angular/core';
import {OrganizationService} from '../../../../shared/services/organization.service';
import {Organization} from '../../../../shared/model/organization';
import {ApiResponse} from '../../../../shared/model/api-response';
import {AlertService} from '../../../../shared/services/alert.service';
import {ConfirmDialogService} from '../../../../shared/services/confirm-dialog.service';
import {Messages} from '../../../../shared/constants/messages';
import {NgClass} from '@angular/common';
import {RouterLink} from '@angular/router';
import {PermissionService} from '../../../../core/services/permission.service';
import {SystemPower} from '../../../../shared/model/permissions';

@Component({
  selector: 'app-organization-admin',
  standalone: true,
  imports: [
    NgClass,
    RouterLink
  ],
  templateUrl: './organization-admin.component.html',
  styleUrl: './organization-admin.component.css'
})
export class OrganizationAdminComponent implements OnInit {

  protected organizations = signal<Array<Organization>>([]);
  protected isLoading = signal<boolean>(false);

  private organizationService: OrganizationService = inject(OrganizationService);
  private alertService: AlertService = inject(AlertService);
  private confirmDialogService: ConfirmDialogService = inject(ConfirmDialogService);
  private permissionService: PermissionService = inject(PermissionService);

  ngOnInit(): void {
    this.loadOrganizations();
  }

  loadOrganizations() {
    this.isLoading.set(true);
    const isSysAdmin = this.permissionService.hasSystemPower(SystemPower.ADMINISTRATOR);
    const request$ = isSysAdmin
      ? this.organizationService.getAllOrganizations(true)
      : this.organizationService.getMyOrganizations();

    request$.subscribe({
      next: (response: ApiResponse<Array<Organization>>) => {
        if (response.success && response.data) {
          const organizations = isSysAdmin
            ? response.data
            : response.data.filter((org) => org.admin || org.moderator || org.owner);
          this.organizations.set(organizations);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.alertService.showError('Errore nel caricamento delle organizzazioni');
      }
    });
  }

  async onDelete(organization: Organization) {
    if (!this.canDelete(organization)) {
      return;
    }
    const accepted = await this.confirmDialogService.confirm({
      title: Messages.CONFIRM_ORGANIZATION_DELETE_TITLE,
      message: Messages.confirmOrganizationDelete(organization.organizationName),
      confirmLabel: Messages.CONFIRM_DELETE,
    });
    if (!accepted) {
      return;
    }
    this.organizationService.deleteOrganization(organization.id).subscribe({
      next: (response: ApiResponse<string>) => {
        if (response.success) {
          this.alertService.showSuccess(Messages.ORGANIZATION_DELETED_SUCCESS);
          this.loadOrganizations();
        }
      }
    });
  }

  protected canCreate(): boolean {
    return this.permissionService.canCreateOrganization();
  }

  protected canDelete(organization: Organization): boolean {
    return this.permissionService.canDeleteOrg(organization);
  }

  protected canOpenEdit(organization: Organization): boolean {
    return this.permissionService.canEditOrg(organization)
      || this.permissionService.canManageOrgMembers(organization);
  }
}
